// src/pages/Board.jsx
import React, {
  useState, useEffect, useRef, useCallback, useMemo,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { db, storage } from '../firebase';
import {
  doc, getDoc, setDoc, collection, query, getDocs,
  addDoc, updateDoc, deleteDoc, orderBy,
} from 'firebase/firestore';
import {
  ref, uploadBytes, getDownloadURL, deleteObject,
} from 'firebase/storage';

/* ================================================================== */
/* Constants                                                           */
/* ================================================================== */

const EMPTY_PRESIDENT = {
  name: '',
  education: '',
  position: '',
  credentials: '',
  linkedin: '',
  photoUrl: '',
};

/* ================================================================== */
/* Theme — per-year accent color                                       */
/* ================================================================== */

const DEFAULT_THEME = {
  primary: '#f97316',      // orange-500 (current default)
  primaryDark: '#ea580c',  // orange-600
  primaryLight: '#fb923c', // orange-400
};

const THEME_PRESETS = [
  { name: 'Orange', primary: '#f97316', primaryDark: '#ea580c', primaryLight: '#fb923c' },
  { name: 'Blue',   primary: '#3b82f6', primaryDark: '#2563eb', primaryLight: '#60a5fa' },
  { name: 'Green',  primary: '#10b981', primaryDark: '#059669', primaryLight: '#34d399' },
  { name: 'Purple', primary: '#8b5cf6', primaryDark: '#7c3aed', primaryLight: '#a78bfa' },
  { name: 'Rose',   primary: '#f43f5e', primaryDark: '#e11d48', primaryLight: '#fb7185' },
  { name: 'Amber',  primary: '#f59e0b', primaryDark: '#d97706', primaryLight: '#fbbf24' },
  { name: 'Teal',   primary: '#14b8a6', primaryDark: '#0d9488', primaryLight: '#2dd4bf' },
  { name: 'Indigo', primary: '#6366f1', primaryDark: '#4f46e5', primaryLight: '#818cf8' },
];

const EMPTY_YEAR_DATA = {
  president: { ...EMPTY_PRESIDENT },
  vicePresidents: [],
  officers: [],
  specialPositions: [],
  theme: { ...DEFAULT_THEME },
};

const PLACEHOLDER = 'https://via.placeholder.com';

/* ================================================================== */
/* Helpers                                                             */
/* ================================================================== */

const normalizeUrl = (url) => {
  if (!url) return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

/**
 * Extract the Firebase Storage object path from a download URL.
 * Download URLs look like:
 *   https://firebasestorage.googleapis.com/v0/b/<bucket>/o/<encoded-path>?...
 * Returns null if the input is not a Firebase Storage download URL.
 */
const getStoragePathFromDownloadUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  try {
    const match = url.match(/\/o\/([^?]+)/);
    if (!match) return null;
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
};

/** Lighten (positive %) or darken (negative %) a hex color. */
const shade = (hex, percent) => {
  if (!hex || typeof hex !== 'string') return hex;
  const h = hex.replace('#', '');
  if (!/^[0-9a-f]{3}$|^[0-9a-f]{6}$/i.test(h)) return hex;
  const num = parseInt(
    h.length === 3 ? h.split('').map((c) => c + c).join('') : h,
    16,
  );
  const r = Math.max(0, Math.min(255, ((num >> 16) & 0xff) + Math.round(2.55 * percent)));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + Math.round(2.55 * percent)));
  const b = Math.max(0, Math.min(255, (num & 0xff) + Math.round(2.55 * percent)));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

/* ================================================================== */
/* Toast                                                               */
/* ================================================================== */

const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);

  const config =
    {
      success: { bg: 'bg-green-500', icon: 'fa-circle-check' },
      error: { bg: 'bg-red-500', icon: 'fa-circle-exclamation' },
      warning: { bg: 'bg-yellow-500', icon: 'fa-triangle-exclamation' },
      info: { bg: 'bg-blue-500', icon: 'fa-circle-info' },
    }[type] || { bg: 'bg-green-500', icon: 'fa-circle-check' };

  return (
    <motion.div
      role="status"
      aria-live="polite"
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 340, damping: 26 }}
      className={`fixed bottom-4 right-4 z-[200] px-4 py-3 rounded-lg shadow-lg text-white flex items-center gap-2 max-w-sm ${config.bg}`}
    >
      <i className={`fas ${config.icon}`} aria-hidden="true" />
      <span className="text-sm font-medium flex-1">{message}</span>
      <button
        type="button"
        onClick={onClose}
        aria-label="Dismiss"
        className="text-white/80 hover:text-white"
      >
        <i className="fas fa-times text-xs" aria-hidden="true" />
      </button>
    </motion.div>
  );
};

/* ================================================================== */
/* Confirm Dialog                                                      */
/* ================================================================== */

const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  onConfirm,
  onCancel,
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-[150] bg-black/60 flex items-center justify-center p-4"
          role="alertdialog"
          aria-modal="true"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 8 }}
            transition={{ type: 'spring', stiffness: 340, damping: 26 }}
            className="w-full max-w-sm bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-5"
          >
            <div className="flex items-start gap-3 mb-4">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  danger
                    ? 'bg-red-100 dark:bg-red-900/30'
                    : 'bg-orange/10'
                }`}
              >
                <i
                  className={`fas ${
                    danger
                      ? 'fa-exclamation-triangle text-red-600 dark:text-red-400'
                      : 'fa-question-circle text-orange'
                  }`}
                  aria-hidden="true"
                />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-1">
                  {title}
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                  {message}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                onClick={onConfirm}
                className={`px-4 py-2 rounded-lg text-sm font-semibold text-white transition ${
                  danger
                    ? 'bg-red-500 hover:bg-red-600'
                    : 'bg-orange hover:bg-orange-dark'
                }`}
              >
                {confirmLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ================================================================== */
/* Prompt Dialog — replaces window.prompt                              */
/* ================================================================== */

const PromptDialog = ({
  open,
  title,
  placeholder,
  defaultValue = '',
  validate,
  onSubmit,
  onCancel,
}) => {
  const [value, setValue] = useState(defaultValue);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setValue(defaultValue);
      setError('');
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open, defaultValue]);

  const handleSubmit = () => {
    const err = validate ? validate(value) : '';
    if (err) {
      setError(err);
      return;
    }
    onSubmit(value.trim());
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-[150] bg-black/60 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 8 }}
            transition={{ type: 'spring', stiffness: 340, damping: 26 }}
            className="w-full max-w-sm bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-5"
          >
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">
              {title}
            </h3>
            <input
              ref={inputRef}
              type="text"
              value={value}
              placeholder={placeholder}
              onChange={(e) => {
                setValue(e.target.value);
                if (error) setError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSubmit();
                if (e.key === 'Escape') onCancel?.();
              }}
              className="w-full p-2.5 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
            />
            {error && (
              <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                <i className="fas fa-exclamation-circle" aria-hidden="true" />
                {error}
              </p>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-orange hover:bg-orange-dark text-white transition"
              >
                Confirm
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ================================================================== */
/* Skeleton                                                            */
/* ================================================================== */

const BoardSkeleton = () => (
  <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 pt-8 pb-16">
    <div className="container mx-auto px-4 max-w-6xl">
      <div className="text-center mb-12">
        <div className="h-10 w-64 mx-auto bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-12 animate-pulse">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="w-40 h-40 rounded-full bg-gray-200 dark:bg-gray-700" />
          <div className="flex-1 space-y-3 w-full">
            <div className="h-6 w-48 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-16 bg-gray-200 dark:bg-gray-700 rounded-xl"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-5 text-center animate-pulse"
          >
            <div className="w-28 h-28 rounded-full bg-gray-200 dark:bg-gray-700 mx-auto mb-3" />
            <div className="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded mx-auto mb-2" />
            <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded mx-auto" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ================================================================== */
/* Empty State                                                         */
/* ================================================================== */

const EmptyState = ({ icon, title, hint }) => (
  <div className="text-center py-10 bg-white/50 dark:bg-gray-800/50 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700">
    <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
      <i className={`fas ${icon} text-xl text-gray-400`} aria-hidden="true" />
    </div>
    <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
      {title}
    </p>
    {hint && (
      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{hint}</p>
    )}
  </div>
);

/* ================================================================== */
/* Main Component                                                      */
/* ================================================================== */

const Board = () => {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [currentYear, setCurrentYear] = useState('2026');
  const [years, setYears] = useState(['2026']);
  const [boardData, setBoardData] = useState({ ...EMPTY_YEAR_DATA });
  const [formerPresidents, setFormerPresidents] = useState([]);

  const [toast, setToast] = useState(null);
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  const [confirm, setConfirm] = useState(null);
  const [prompt, setPrompt] = useState(null);

  const [presidentModal, setPresidentModal] = useState(false);
  const [vpModal, setVpModal] = useState(false);
  const [officerModal, setOfficerModal] = useState(false);
  const [specialModal, setSpecialModal] = useState(false);
  const [editVpModal, setEditVpModal] = useState(false);
  const [editOfficerModal, setEditOfficerModal] = useState(false);
  const [editSpecialModal, setEditSpecialModal] = useState(false);
  const [detailModal, setDetailModal] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [formerModalOpen, setFormerModalOpen] = useState(false);
  const [editingFormer, setEditingFormer] = useState(null);
  const [formerForm, setFormerForm] = useState({
    name: '',
    year: '',
    linkedin: '',
    imageFile: null,
    imagePreview: '',
  });
  const [submittingFormer, setSubmittingFormer] = useState(false);

  /* ----- Color / theme modal state ----- */
  const [colorModalOpen, setColorModalOpen] = useState(false);
  const [colorDraft, setColorDraft] = useState({ ...DEFAULT_THEME });

  const [presidentForm, setPresidentForm] = useState({
    name: '',
    education: '',
    position: '',
    credentials: '',
    linkedin: '',
    photoFile: null,
  });
  const [vpForm, setVpForm] = useState({
    title: '',
    name: '',
    education: '',
    position: '',
    credentials: '',
    linkedin: '',
    photoFile: null,
  });
  const [officerForm, setOfficerForm] = useState({
    position: '',
    name: '',
    education: '',
    currentPosition: '',
    credentials: '',
    linkedin: '',
    photoFile: null,
  });
  const [specialForm, setSpecialForm] = useState({
    position: '',
    name: '',
    education: '',
    currentPosition: '',
    credentials: '',
    linkedin: '',
    photoFile: null,
  });
  const [editVpIndex, setEditVpIndex] = useState(null);
  const [editOfficerIndex, setEditOfficerIndex] = useState(null);
  const [editSpecialIndex, setEditSpecialIndex] = useState(null);
  const [editVpForm, setEditVpForm] = useState({
    title: '',
    name: '',
    education: '',
    position: '',
    credentials: '',
    linkedin: '',
    photoFile: null,
  });
  const [editOfficerForm, setEditOfficerForm] = useState({
    position: '',
    name: '',
    education: '',
    currentPosition: '',
    credentials: '',
    linkedin: '',
    photoFile: null,
  });
  const [editSpecialForm, setEditSpecialForm] = useState({
    position: '',
    name: '',
    education: '',
    currentPosition: '',
    credentials: '',
    linkedin: '',
    photoFile: null,
  });

  const [submitting, setSubmitting] = useState(false);

  /* ================================================================== */
  /* Data fetching                                                       */
  /* ================================================================== */

  const fetchYears = useCallback(async () => {
    try {
      const q = query(collection(db, 'boardOfDirectors'));
      const snapshot = await getDocs(q);
      const yearList = snapshot.docs
        .map((d) => d.id)
        .filter((id) => /^\d{4}$/.test(id))
        .sort((a, b) => Number(b) - Number(a));
      if (yearList.length === 0) yearList.push('2026');
      setYears(yearList);
      setCurrentYear((prev) =>
        yearList.includes(prev) ? prev : yearList[0],
      );
    } catch (err) {
      console.error('fetchYears failed:', err);
      showToast('Failed to load years', 'error');
    }
  }, [showToast]);

  const fetchBoardData = useCallback(
    async (year) => {
      if (!year) return;
      setLoading(true);
      try {
        const docRef = doc(db, 'boardOfDirectors', year);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setBoardData({
            president: data.president || { ...EMPTY_PRESIDENT },
            vicePresidents: data.vicePresidents || [],
            officers: data.officers || [],
            specialPositions: data.specialPositions || [],
            theme: { ...DEFAULT_THEME, ...(data.theme || {}) },
          });
        } else {
          setBoardData({ ...EMPTY_YEAR_DATA });
        }
      } catch (err) {
        console.error('fetchBoardData failed:', err);
        showToast('Failed to load board data', 'error');
      } finally {
        setLoading(false);
      }
    },
    [showToast],
  );

  const fetchFormerPresidents = useCallback(async () => {
    try {
      const q = query(
        collection(db, 'formerPresidents'),
        orderBy('year', 'desc'),
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setFormerPresidents(data);
    } catch (err) {
      console.error('fetchFormerPresidents failed:', err);
    }
  }, []);

  useEffect(() => {
    fetchYears();
  }, [fetchYears]);

  useEffect(() => {
    fetchBoardData(currentYear);
  }, [currentYear, fetchBoardData]);

  useEffect(() => {
    fetchFormerPresidents();
  }, [fetchFormerPresidents]);

  const saveBoardData = useCallback(
    async (newData) => {
      const docRef = doc(db, 'boardOfDirectors', currentYear);
      await setDoc(docRef, newData, { merge: true });
      setBoardData(newData);
    },
    [currentYear],
  );

  const hasPresident = useMemo(
    () =>
      Boolean(
        boardData.president &&
          (boardData.president.name || boardData.president.photoUrl),
      ),
    [boardData.president],
  );

  /* ================================================================== */
  /* Image cleanup helper                                                */
  /* ================================================================== */

  const tryDeleteStorageFile = useCallback(async (url) => {
    if (!url || typeof url !== 'string') return;
    if (!url.startsWith('http')) return;
    const path = getStoragePathFromDownloadUrl(url);
    if (!path) return;
    try {
      await deleteObject(ref(storage, path));
    } catch (e) {
      console.warn('deleteObject failed (non-fatal):', e?.message);
    }
  }, []);

  /* ================================================================== */
  /* President                                                           */
  /* ================================================================== */

  const openPresidentModal = () => {
    setPresidentForm({
      name: boardData.president?.name || '',
      education: boardData.president?.education || '',
      position: boardData.president?.position || '',
      credentials: boardData.president?.credentials || '',
      linkedin: boardData.president?.linkedin || '',
      photoFile: null,
    });
    setPresidentModal(true);
  };

  const handlePresidentSave = async () => {
    if (!isAdmin) return;
    if (!presidentForm.name.trim()) {
      showToast('President name is required.', 'warning');
      return;
    }
    setSubmitting(true);
    try {
      let photoUrl = boardData.president?.photoUrl || '';
      if (presidentForm.photoFile) {
        const storageRef = ref(
          storage,
          `bod/${currentYear}/president_${Date.now()}_${presidentForm.photoFile.name}`,
        );
        await uploadBytes(storageRef, presidentForm.photoFile);
        photoUrl = await getDownloadURL(storageRef);
        if (
          boardData.president?.photoUrl &&
          boardData.president.photoUrl !== photoUrl
        ) {
          await tryDeleteStorageFile(boardData.president.photoUrl);
        }
      }
      const updatedPresident = {
        name: presidentForm.name.trim(),
        education: presidentForm.education.trim(),
        position: presidentForm.position.trim(),
        credentials: presidentForm.credentials.trim(),
        linkedin: presidentForm.linkedin.trim(),
        photoUrl,
      };
      await saveBoardData({ ...boardData, president: updatedPresident });
      setPresidentModal(false);
      showToast('President saved', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to save president', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  /* ================================================================== */
  /* Vice Presidents                                                     */
  /* ================================================================== */

  const openAddVpModal = () => {
    setVpForm({
      title: '',
      name: '',
      education: '',
      position: '',
      credentials: '',
      linkedin: '',
      photoFile: null,
    });
    setVpModal(true);
  };

  const handleAddVp = async () => {
    if (!isAdmin) return;
    if (!vpForm.name.trim()) {
      showToast('Name is required.', 'warning');
      return;
    }
    setSubmitting(true);
    try {
      let photoUrl = '';
      if (vpForm.photoFile) {
        const storageRef = ref(
          storage,
          `bod/${currentYear}/vp_${Date.now()}_${vpForm.photoFile.name}`,
        );
        await uploadBytes(storageRef, vpForm.photoFile);
        photoUrl = await getDownloadURL(storageRef);
      }
      const newVp = {
        title: vpForm.title,
        name: vpForm.name,
        education: vpForm.education,
        position: vpForm.position,
        credentials: vpForm.credentials,
        linkedin: vpForm.linkedin,
        photoUrl,
      };
      await saveBoardData({
        ...boardData,
        vicePresidents: [...(boardData.vicePresidents || []), newVp],
      });
      setVpModal(false);
      showToast('Vice President added', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to add VP', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditVpModal = (idx) => {
    const vp = boardData.vicePresidents[idx];
    setEditVpIndex(idx);
    setEditVpForm({
      title: vp.title || '',
      name: vp.name || '',
      education: vp.education || '',
      position: vp.position || '',
      credentials: vp.credentials || '',
      linkedin: vp.linkedin || '',
      photoFile: null,
    });
    setEditVpModal(true);
  };

  const handleUpdateVp = async () => {
    if (!isAdmin) return;
    setSubmitting(true);
    try {
      const currentVp = boardData.vicePresidents[editVpIndex];
      let photoUrl = currentVp.photoUrl || '';
      if (editVpForm.photoFile) {
        const storageRef = ref(
          storage,
          `bod/${currentYear}/vp_${Date.now()}_${editVpForm.photoFile.name}`,
        );
        await uploadBytes(storageRef, editVpForm.photoFile);
        photoUrl = await getDownloadURL(storageRef);
        if (currentVp.photoUrl && currentVp.photoUrl !== photoUrl) {
          await tryDeleteStorageFile(currentVp.photoUrl);
        }
      }
      const updatedVp = {
        title: editVpForm.title,
        name: editVpForm.name,
        education: editVpForm.education,
        position: editVpForm.position,
        credentials: editVpForm.credentials,
        linkedin: editVpForm.linkedin,
        photoUrl,
      };
      const newVps = [...boardData.vicePresidents];
      newVps[editVpIndex] = updatedVp;
      await saveBoardData({ ...boardData, vicePresidents: newVps });
      setEditVpModal(false);
      showToast('Vice President updated', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to update VP', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteVp = (idx) => {
    if (!isAdmin) return;
    setConfirm({
      title: 'Delete Vice President?',
      message: `Remove "${boardData.vicePresidents[idx].name}" from ${currentYear}?`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        setConfirm(null);
        try {
          const vp = boardData.vicePresidents[idx];
          if (vp.photoUrl) await tryDeleteStorageFile(vp.photoUrl);
          await saveBoardData({
            ...boardData,
            vicePresidents: boardData.vicePresidents.filter(
              (_, i) => i !== idx,
            ),
          });
          showToast('Vice President removed', 'success');
        } catch (err) {
          console.error(err);
          showToast('Failed to delete VP', 'error');
        }
      },
    });
  };

  /* ================================================================== */
  /* Officers                                                            */
  /* ================================================================== */

  const openAddOfficerModal = () => {
    setOfficerForm({
      position: '',
      name: '',
      education: '',
      currentPosition: '',
      credentials: '',
      linkedin: '',
      photoFile: null,
    });
    setOfficerModal(true);
  };

  const handleAddOfficer = async () => {
    if (!isAdmin) return;
    if (!officerForm.name.trim()) {
      showToast('Name is required.', 'warning');
      return;
    }
    setSubmitting(true);
    try {
      let photoUrl = '';
      if (officerForm.photoFile) {
        const storageRef = ref(
          storage,
          `bod/${currentYear}/officer_${Date.now()}_${officerForm.photoFile.name}`,
        );
        await uploadBytes(storageRef, officerForm.photoFile);
        photoUrl = await getDownloadURL(storageRef);
      }
      const newOfficer = {
        position: officerForm.position,
        name: officerForm.name,
        education: officerForm.education,
        currentPosition: officerForm.currentPosition,
        credentials: officerForm.credentials,
        linkedin: officerForm.linkedin,
        photoUrl,
      };
      await saveBoardData({
        ...boardData,
        officers: [...(boardData.officers || []), newOfficer],
      });
      setOfficerModal(false);
      showToast('Officer added', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to add officer', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditOfficerModal = (idx) => {
    const off = boardData.officers[idx];
    setEditOfficerIndex(idx);
    setEditOfficerForm({
      position: off.position || '',
      name: off.name || '',
      education: off.education || '',
      currentPosition: off.currentPosition || '',
      credentials: off.credentials || '',
      linkedin: off.linkedin || '',
      photoFile: null,
    });
    setEditOfficerModal(true);
  };

  const handleUpdateOfficer = async () => {
    if (!isAdmin) return;
    setSubmitting(true);
    try {
      const current = boardData.officers[editOfficerIndex];
      let photoUrl = current.photoUrl || '';
      if (editOfficerForm.photoFile) {
        const storageRef = ref(
          storage,
          `bod/${currentYear}/officer_${Date.now()}_${editOfficerForm.photoFile.name}`,
        );
        await uploadBytes(storageRef, editOfficerForm.photoFile);
        photoUrl = await getDownloadURL(storageRef);
        if (current.photoUrl && current.photoUrl !== photoUrl) {
          await tryDeleteStorageFile(current.photoUrl);
        }
      }
      const updatedOfficer = {
        position: editOfficerForm.position,
        name: editOfficerForm.name,
        education: editOfficerForm.education,
        currentPosition: editOfficerForm.currentPosition,
        credentials: editOfficerForm.credentials,
        linkedin: editOfficerForm.linkedin,
        photoUrl,
      };
      const newOfficers = [...boardData.officers];
      newOfficers[editOfficerIndex] = updatedOfficer;
      await saveBoardData({ ...boardData, officers: newOfficers });
      setEditOfficerModal(false);
      showToast('Officer updated', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to update officer', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteOfficer = (idx) => {
    if (!isAdmin) return;
    setConfirm({
      title: 'Delete Officer?',
      message: `Remove "${boardData.officers[idx].name}" from ${currentYear}?`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        setConfirm(null);
        try {
          const off = boardData.officers[idx];
          if (off.photoUrl) await tryDeleteStorageFile(off.photoUrl);
          await saveBoardData({
            ...boardData,
            officers: boardData.officers.filter((_, i) => i !== idx),
          });
          showToast('Officer removed', 'success');
        } catch (err) {
          console.error(err);
          showToast('Failed to delete officer', 'error');
        }
      },
    });
  };

  /* ================================================================== */
  /* Special Positions                                                   */
  /* ================================================================== */

  const openAddSpecialModal = () => {
    setSpecialForm({
      position: '',
      name: '',
      education: '',
      currentPosition: '',
      credentials: '',
      linkedin: '',
      photoFile: null,
    });
    setSpecialModal(true);
  };

  const handleAddSpecial = async () => {
    if (!isAdmin) return;
    if (!specialForm.name.trim()) {
      showToast('Name is required.', 'warning');
      return;
    }
    setSubmitting(true);
    try {
      let photoUrl = '';
      if (specialForm.photoFile) {
        const storageRef = ref(
          storage,
          `bod/${currentYear}/special_${Date.now()}_${specialForm.photoFile.name}`,
        );
        await uploadBytes(storageRef, specialForm.photoFile);
        photoUrl = await getDownloadURL(storageRef);
      }
      const newSpecial = {
        position: specialForm.position,
        name: specialForm.name,
        education: specialForm.education,
        currentPosition: specialForm.currentPosition,
        credentials: specialForm.credentials,
        linkedin: specialForm.linkedin,
        photoUrl,
      };
      await saveBoardData({
        ...boardData,
        specialPositions: [
          ...(boardData.specialPositions || []),
          newSpecial,
        ],
      });
      setSpecialModal(false);
      showToast('Special position added', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to add special position', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditSpecialModal = (idx) => {
    const sp = boardData.specialPositions[idx];
    setEditSpecialIndex(idx);
    setEditSpecialForm({
      position: sp.position || '',
      name: sp.name || '',
      education: sp.education || '',
      currentPosition: sp.currentPosition || '',
      credentials: sp.credentials || '',
      linkedin: sp.linkedin || '',
      photoFile: null,
    });
    setEditSpecialModal(true);
  };

  const handleUpdateSpecial = async () => {
    if (!isAdmin) return;
    setSubmitting(true);
    try {
      const current = boardData.specialPositions[editSpecialIndex];
      let photoUrl = current.photoUrl || '';
      if (editSpecialForm.photoFile) {
        const storageRef = ref(
          storage,
          `bod/${currentYear}/special_${Date.now()}_${editSpecialForm.photoFile.name}`,
        );
        await uploadBytes(storageRef, editSpecialForm.photoFile);
        photoUrl = await getDownloadURL(storageRef);
        if (current.photoUrl && current.photoUrl !== photoUrl) {
          await tryDeleteStorageFile(current.photoUrl);
        }
      }
      const updatedSpecial = {
        position: editSpecialForm.position,
        name: editSpecialForm.name,
        education: editSpecialForm.education,
        currentPosition: editSpecialForm.currentPosition,
        credentials: editSpecialForm.credentials,
        linkedin: editSpecialForm.linkedin,
        photoUrl,
      };
      const newSpecials = [...boardData.specialPositions];
      newSpecials[editSpecialIndex] = updatedSpecial;
      await saveBoardData({ ...boardData, specialPositions: newSpecials });
      setEditSpecialModal(false);
      showToast('Special position updated', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to update special position', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSpecial = (idx) => {
    if (!isAdmin) return;
    setConfirm({
      title: 'Delete Special Position?',
      message: `Remove "${boardData.specialPositions[idx].name}" from ${currentYear}?`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        setConfirm(null);
        try {
          const sp = boardData.specialPositions[idx];
          if (sp.photoUrl) await tryDeleteStorageFile(sp.photoUrl);
          await saveBoardData({
            ...boardData,
            specialPositions: boardData.specialPositions.filter(
              (_, i) => i !== idx,
            ),
          });
          showToast('Special position removed', 'success');
        } catch (err) {
          console.error(err);
          showToast('Failed to delete special position', 'error');
        }
      },
    });
  };

  /* ================================================================== */
  /* Former Presidents                                                   */
  /* ================================================================== */

  const openFormerModal = (president = null) => {
    if (president) {
      setEditingFormer(president);
      setFormerForm({
        name: president.name,
        year: president.year,
        linkedin: president.linkedin || '',
        imageFile: null,
        imagePreview: president.imageUrl,
      });
    } else {
      setEditingFormer(null);
      setFormerForm({
        name: '',
        year: '',
        linkedin: '',
        imageFile: null,
        imagePreview: '',
      });
    }
    setFormerModalOpen(true);
  };

  const handleFormerImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormerForm({
        ...formerForm,
        imageFile: file,
        imagePreview: URL.createObjectURL(file),
      });
    }
  };

  const handleSaveFormer = async () => {
    if (!isAdmin) return;
    if (!formerForm.name.trim() || !formerForm.year.trim()) {
      showToast('Name and year are required', 'warning');
      return;
    }
    setSubmittingFormer(true);
    try {
      let imageUrl =
        formerForm.imagePreview && !formerForm.imageFile
          ? formerForm.imagePreview
          : '';
      if (formerForm.imageFile) {
        const storageRef = ref(
          storage,
          `formerPresidents/${Date.now()}_${formerForm.imageFile.name}`,
        );
        await uploadBytes(storageRef, formerForm.imageFile);
        imageUrl = await getDownloadURL(storageRef);
        if (
          editingFormer &&
          editingFormer.imageUrl &&
          editingFormer.imageUrl !== imageUrl
        ) {
          await tryDeleteStorageFile(editingFormer.imageUrl);
        }
      }
      const data = {
        name: formerForm.name.trim(),
        year: formerForm.year.trim(),
        linkedin: formerForm.linkedin.trim(),
        imageUrl,
      };
      if (editingFormer) {
        await updateDoc(doc(db, 'formerPresidents', editingFormer.id), data);
      } else {
        await addDoc(collection(db, 'formerPresidents'), data);
      }
      await fetchFormerPresidents();
      setFormerModalOpen(false);
      showToast(
        editingFormer ? 'Former President updated' : 'Former President added',
        'success',
      );
    } catch (err) {
      console.error(err);
      showToast('Failed to save', 'error');
    } finally {
      setSubmittingFormer(false);
    }
  };

  const handleDeleteFormer = (president) => {
    if (!isAdmin) return;
    setConfirm({
      title: 'Delete Former President?',
      message: `Remove "${president.name}" (${president.year})?`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        setConfirm(null);
        try {
          if (president.imageUrl) {
            await tryDeleteStorageFile(president.imageUrl);
          }
          await deleteDoc(doc(db, 'formerPresidents', president.id));
          await fetchFormerPresidents();
          showToast('Former President removed', 'success');
        } catch (err) {
          console.error(err);
          showToast('Delete failed', 'error');
        }
      },
    });
  };

  /* ================================================================== */
  /* Year management                                                     */
  /* ================================================================== */

  const addNewYear = () => {
    if (!isAdmin) return;
    setPrompt({
      title: 'Add New Year',
      placeholder: 'e.g., 2027',
      validate: (v) => {
        const t = v.trim();
        if (!/^\d{4}$/.test(t)) return 'Enter a 4-digit year';
        if (years.includes(t)) return 'That year already exists';
        return '';
      },
      onSubmit: async (newYear) => {
        setPrompt(null);
        try {
          const newYears = [...years, newYear]
            .filter((y) => /^\d{4}$/.test(y))
            .sort((a, b) => Number(b) - Number(a));
          setYears(newYears);
          setCurrentYear(newYear);
          await setDoc(doc(db, 'boardOfDirectors', newYear), {
            president: { ...EMPTY_PRESIDENT },
            vicePresidents: [],
            officers: [],
            specialPositions: [],
            theme: { ...DEFAULT_THEME },
          });
          setBoardData({ ...EMPTY_YEAR_DATA });
          showToast(`Year ${newYear} added`, 'success');
        } catch (err) {
          console.error(err);
          showToast('Failed to add year', 'error');
        }
      },
    });
  };

  const deleteCurrentYear = () => {
    if (!isAdmin) return;
    setConfirm({
      title: `Reset ${currentYear}?`,
      message: `This will delete all board data for ${currentYear}. This cannot be undone.`,
      confirmLabel: 'Reset Year',
      danger: true,
      onConfirm: async () => {
        setConfirm(null);
        try {
          // Preserve the current theme so the admin keeps their color choice.
          const preservedTheme =
            boardData.theme && boardData.theme.primary
              ? { ...boardData.theme }
              : { ...DEFAULT_THEME };
          await setDoc(doc(db, 'boardOfDirectors', currentYear), {
            president: { ...EMPTY_PRESIDENT },
            vicePresidents: [],
            officers: [],
            specialPositions: [],
            theme: preservedTheme,
          });
          setBoardData({ ...EMPTY_YEAR_DATA, theme: preservedTheme });
          showToast(`${currentYear} has been reset`, 'success');
        } catch (err) {
          console.error(err);
          showToast('Failed to reset year', 'error');
        }
      },
    });
  };

  /* ================================================================== */
  /* Theme color                                                         */
  /* ================================================================== */

  const openColorModal = () => {
    if (!isAdmin) return;
    setColorDraft({ ...(boardData.theme || DEFAULT_THEME) });
    setColorModalOpen(true);
  };

  const handleSaveColor = async () => {
    if (!isAdmin) return;
    try {
      // Validate the hex code, fall back to default if invalid
      const primary = /^#[0-9a-f]{6}$/i.test(colorDraft.primary)
        ? colorDraft.primary
        : DEFAULT_THEME.primary;
      const nextTheme = {
        primary,
        primaryDark: colorDraft.primaryDark || shade(primary, -15),
        primaryLight: colorDraft.primaryLight || shade(primary, 20),
      };
      await saveBoardData({ ...boardData, theme: nextTheme });
      setColorModalOpen(false);
      showToast('Theme color updated', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to save color', 'error');
    }
  };

  /* ================================================================== */
  /* Detail modal                                                        */
  /* ================================================================== */

  const showDetail = (type, data) => {
    setDetailData({ type, ...data });
    setDetailModal(true);
  };

  /* ================================================================== */
  /* Render                                                              */
  /* ================================================================== */

  const activeTheme = boardData.theme || DEFAULT_THEME;

  if (loading) return <BoardSkeleton />;

  return (
    <div
      className="board-themed min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 pt-8 pb-16"
      style={{
        '--color-primary': activeTheme.primary,
        '--color-primary-dark': activeTheme.primaryDark,
        '--color-primary-light': activeTheme.primaryLight,
      }}
    >
      {/* Per-year theme override — maps Tailwind's `orange` utilities to CSS vars */}
      <style>{`
        .board-themed .text-orange { color: var(--color-primary) !important; }
        .board-themed .bg-orange { background-color: var(--color-primary) !important; }
        .board-themed .bg-orange\\/10 { background-color: color-mix(in srgb, var(--color-primary) 10%, transparent) !important; }
        .board-themed .bg-orange\\/20 { background-color: color-mix(in srgb, var(--color-primary) 20%, transparent) !important; }
        .board-themed .border-orange { border-color: var(--color-primary) !important; }
        .board-themed .border-orange\\/40 { border-color: color-mix(in srgb, var(--color-primary) 40%, transparent) !important; }
        .board-themed .hover\\:bg-orange:hover { background-color: var(--color-primary) !important; }
        .board-themed .hover\\:bg-orange-dark:hover { background-color: var(--color-primary-dark) !important; }
        .board-themed .hover\\:border-orange:hover { border-color: var(--color-primary) !important; }
        .board-themed .focus\\:ring-orange:focus { --tw-ring-color: var(--color-primary) !important; }
        .board-themed .from-orange { --tw-gradient-from: var(--color-primary) !important; }
        .board-themed .to-orange { --tw-gradient-to: var(--color-primary) !important; }
        .board-themed .to-orange-dark { --tw-gradient-to: var(--color-primary-dark) !important; }
      `}</style>

      <AnimatePresence>
        {toast && (
          <Toast
            key="toast"
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </AnimatePresence>

      <div className="container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 dark:text-white relative inline-block">
            Board of Directors
            <span className="absolute -bottom-2 left-0 w-1/2 h-1 bg-orange rounded-full" />
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-3">
            Leadership driving excellence in electrical engineering
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-4 mb-12 items-center">
          <select
            value={currentYear}
            onChange={(e) => setCurrentYear(e.target.value)}
            aria-label="Select year"
            className="bg-white dark:bg-gray-800 text-gray-800 dark:text-white border border-gray-300 dark:border-gray-600 rounded-full px-5 py-2 focus:outline-none focus:ring-2 focus:ring-orange"
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          {isAdmin && (
            <>
              <button
                type="button"
                onClick={addNewYear}
                className="bg-orange hover:bg-orange-dark text-white px-4 py-2 rounded-full text-sm font-semibold transition"
              >
                <i className="fas fa-plus mr-1" aria-hidden="true" /> Add Year
              </button>
              <button
                type="button"
                onClick={openColorModal}
                className="bg-orange hover:bg-orange-dark text-white px-4 py-2 rounded-full text-sm font-semibold transition flex items-center gap-2"
                title="Change accent color for this year"
              >
                <span
                  className="inline-block w-4 h-4 rounded-full border-2 border-white/60"
                  style={{ background: activeTheme.primary }}
                  aria-hidden="true"
                />
                Theme Color
              </button>
              <button
                type="button"
                onClick={deleteCurrentYear}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-full text-sm font-semibold transition"
              >
                <i className="fas fa-trash-alt mr-1" aria-hidden="true" />{' '}
                Reset Year
              </button>
            </>
          )}
        </div>

        {hasPresident ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-12 flex flex-col md:flex-row items-center gap-6"
          >
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-orange to-orange-dark text-white text-xs font-bold px-4 py-1 rounded-full">
              PRESIDENT · {currentYear}
            </div>
            {isAdmin && (
              <div className="absolute top-3 right-3 flex gap-2">
                <button
                  type="button"
                  onClick={openPresidentModal}
                  className="bg-orange/20 hover:bg-orange text-gray-800 dark:text-white p-1.5 rounded-full transition"
                  title="Edit President"
                  aria-label="Edit President"
                >
                  <i className="fas fa-edit" aria-hidden="true" />
                </button>
              </div>
            )}
            <div className="flex-shrink-0">
              <img
                src={
                  boardData.president.photoUrl ||
                  `${PLACEHOLDER}/200x200?text=President`
                }
                alt={boardData.president.name}
                loading="lazy"
                className="w-40 h-40 rounded-full object-cover border-4 border-orange shadow-lg"
              />
            </div>
            <div className="text-center md:text-left flex-1">
              <h2 className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-1">
                {boardData.president.name}
              </h2>
              <p className="text-orange font-semibold uppercase tracking-wide mb-3">
                President · IIEE ERCSA
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                <div className="bg-gray-100 dark:bg-gray-700 rounded-xl p-2 text-left">
                  <div className="text-xs text-orange">Education</div>
                  <div className="font-medium text-sm">
                    {boardData.president.education || 'N/A'}
                  </div>
                </div>
                <div className="bg-gray-100 dark:bg-gray-700 rounded-xl p-2 text-left">
                  <div className="text-xs text-orange">Current Role</div>
                  <div className="font-medium text-sm">
                    {boardData.president.position || 'N/A'}
                  </div>
                </div>
                <div className="bg-gray-100 dark:bg-gray-700 rounded-xl p-2 text-left">
                  <div className="text-xs text-orange">Credentials</div>
                  <div className="font-medium text-sm">
                    {boardData.president.credentials || 'N/A'}
                  </div>
                </div>
              </div>
              {boardData.president.linkedin && (
                <a
                  href={normalizeUrl(boardData.president.linkedin)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-3 text-xs text-orange hover:underline font-medium"
                >
                  <i className="fab fa-linkedin" aria-hidden="true" />
                  View LinkedIn / Website
                </a>
              )}
              <button
                type="button"
                onClick={() => showDetail('president', boardData.president)}
                className="mt-3 ml-3 text-xs text-orange hover:underline font-medium"
              >
                <i className="fas fa-info-circle mr-1" aria-hidden="true" />
                View full details
              </button>
            </div>
          </motion.div>
        ) : (
          <div className="relative bg-white dark:bg-gray-800 rounded-3xl shadow-lg border-2 border-dashed border-orange/40 p-8 mb-12 text-center">
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-orange to-orange-dark text-white text-xs font-bold px-4 py-1 rounded-full">
              PRESIDENT · {currentYear}
            </div>
            <div className="w-24 h-24 rounded-full bg-orange/10 flex items-center justify-center mx-auto mb-4">
              <i
                className="fas fa-user-tie text-orange text-3xl"
                aria-hidden="true"
              />
            </div>
            <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">
              No President Assigned
            </h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
              {isAdmin
                ? 'Add the president for this year to get started.'
                : 'The president for this year has not been assigned yet.'}
            </p>
            {isAdmin && (
              <button
                type="button"
                onClick={openPresidentModal}
                className="bg-orange hover:bg-orange-dark text-white px-5 py-2 rounded-full text-sm font-semibold transition"
              >
                <i className="fas fa-plus mr-1" aria-hidden="true" /> Add
                President
              </button>
            )}
          </div>
        )}

        {/* ==================== VICE PRESIDENTS ==================== */}
        <div className="mb-12">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
              Vice Presidents
            </h2>
            {isAdmin && (
              <button
                type="button"
                onClick={openAddVpModal}
                className="bg-orange hover:bg-orange-dark text-white px-4 py-1.5 rounded-full text-sm font-semibold transition"
              >
                <i className="fas fa-plus mr-1" aria-hidden="true" /> Add VP
              </button>
            )}
          </div>
          {boardData.vicePresidents.length === 0 ? (
            <EmptyState
              icon="fa-user-tie"
              title="No vice presidents added yet"
              hint={
                isAdmin
                  ? 'Click "Add VP" to add one'
                  : 'Check back soon for updates'
              }
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {boardData.vicePresidents.map((vp, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ y: -5 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-5 text-center relative cursor-pointer group"
                  onDoubleClick={() => showDetail('vp', vp)}
                >
                  {isAdmin && (
                    <div className="absolute top-2 right-2 flex gap-1 z-10">
                      <button
                        type="button"
                        onClick={() => openEditVpModal(idx)}
                        className="bg-orange/20 hover:bg-orange p-1 rounded-full text-xs transition"
                        aria-label="Edit VP"
                      >
                        <i className="fas fa-edit" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteVp(idx)}
                        className="bg-red-500/20 hover:bg-red-500 text-white p-1 rounded-full text-xs transition"
                        aria-label="Delete VP"
                      >
                        <i className="fas fa-trash-alt" aria-hidden="true" />
                      </button>
                    </div>
                  )}
                  <img
                    src={vp.photoUrl || `${PLACEHOLDER}/120x120?text=VP`}
                    alt={vp.name}
                    loading="lazy"
                    className="w-28 h-28 rounded-full mx-auto object-cover border-2 border-orange mb-3"
                  />
                  <div className="text-xs text-orange uppercase tracking-wide">
                    {vp.title}
                  </div>
                  <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                    {vp.name}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {vp.credentials}
                  </div>
                  {vp.linkedin && (
                    <a
                      href={normalizeUrl(vp.linkedin)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 mt-2 text-xs text-orange hover:underline"
                      aria-label={`${vp.name} LinkedIn`}
                    >
                      <i className="fab fa-linkedin" aria-hidden="true" /> Profile
                    </a>
                  )}
                  <p className="text-[10px] text-gray-400 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    Double-tap for details
                  </p>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* ==================== OFFICERS ==================== */}
        <div className="mb-12">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
              Officers
            </h2>
            {isAdmin && (
              <button
                type="button"
                onClick={openAddOfficerModal}
                className="bg-orange hover:bg-orange-dark text-white px-4 py-1.5 rounded-full text-sm font-semibold transition"
              >
                <i className="fas fa-plus mr-1" aria-hidden="true" /> Add
                Officer
              </button>
            )}
          </div>
          {boardData.officers.length === 0 ? (
            <EmptyState
              icon="fa-users"
              title="No officers added yet"
              hint={
                isAdmin
                  ? 'Click "Add Officer" to add one'
                  : 'Check back soon for updates'
              }
            />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {boardData.officers.map((off, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ y: -5 }}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4 text-center relative cursor-pointer group"
                  onDoubleClick={() => showDetail('officer', off)}
                >
                  {isAdmin && (
                    <div className="absolute top-2 right-2 flex gap-1 z-10">
                      <button
                        type="button"
                        onClick={() => openEditOfficerModal(idx)}
                        className="bg-orange/20 hover:bg-orange p-1 rounded-full text-xs transition"
                        aria-label="Edit Officer"
                      >
                        <i className="fas fa-edit" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteOfficer(idx)}
                        className="bg-red-500/20 hover:bg-red-500 text-white p-1 rounded-full text-xs transition"
                        aria-label="Delete Officer"
                      >
                        <i className="fas fa-trash-alt" aria-hidden="true" />
                      </button>
                    </div>
                  )}
                  <img
                    src={off.photoUrl || `${PLACEHOLDER}/90x90?text=Officer`}
                    alt={off.name}
                    loading="lazy"
                    className="w-20 h-20 rounded-full mx-auto object-cover border-2 border-orange mb-2"
                  />
                  <div className="text-xs text-orange uppercase">
                    {off.position}
                  </div>
                  <div className="font-bold text-blue-600 dark:text-blue-400">
                    {off.name}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {off.credentials}
                  </div>
                  {off.linkedin && (
                    <a
                      href={normalizeUrl(off.linkedin)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 mt-1.5 text-[11px] text-orange hover:underline"
                      aria-label={`${off.name} LinkedIn`}
                    >
                      <i className="fab fa-linkedin" aria-hidden="true" /> Profile
                    </a>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* ==================== SPECIAL POSITIONS ==================== */}
        <div className="mb-12">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
              Special Positions
            </h2>
            {isAdmin && (
              <button
                type="button"
                onClick={openAddSpecialModal}
                className="bg-orange hover:bg-orange-dark text-white px-4 py-1.5 rounded-full text-sm font-semibold transition"
              >
                <i className="fas fa-plus mr-1" aria-hidden="true" /> Add
                Special
              </button>
            )}
          </div>
          {boardData.specialPositions.length === 0 ? (
            <EmptyState
              icon="fa-star"
              title="No special positions added yet"
              hint={
                isAdmin
                  ? 'Click "Add Special" to add one'
                  : 'Check back soon for updates'
              }
            />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {boardData.specialPositions.map((sp, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ y: -5 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-5 text-center relative cursor-pointer"
                  onDoubleClick={() => showDetail('special', sp)}
                >
                  {isAdmin && (
                    <div className="absolute top-2 right-2 flex gap-1 z-10">
                      <button
                        type="button"
                        onClick={() => openEditSpecialModal(idx)}
                        className="bg-orange/20 hover:bg-orange p-1 rounded-full text-xs transition"
                        aria-label="Edit Special Position"
                      >
                        <i className="fas fa-edit" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteSpecial(idx)}
                        className="bg-red-500/20 hover:bg-red-500 text-white p-1 rounded-full text-xs transition"
                        aria-label="Delete Special Position"
                      >
                        <i className="fas fa-trash-alt" aria-hidden="true" />
                      </button>
                    </div>
                  )}
                  <img
                    src={sp.photoUrl || `${PLACEHOLDER}/120x120?text=Special`}
                    alt={sp.name}
                    loading="lazy"
                    className="w-24 h-24 rounded-full mx-auto object-cover border-2 border-orange mb-3"
                  />
                  <div className="text-orange font-semibold text-sm uppercase">
                    {sp.position}
                  </div>
                  <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                    {sp.name}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {sp.credentials}
                  </div>
                  {sp.linkedin && (
                    <a
                      href={normalizeUrl(sp.linkedin)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 mt-2 text-xs text-orange hover:underline"
                      aria-label={`${sp.name} LinkedIn`}
                    >
                      <i className="fab fa-linkedin" aria-hidden="true" /> Profile
                    </a>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* ==================== FORMER PRESIDENTS ==================== */}
        <div className="mt-16 pt-8 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
              <i className="fas fa-history text-orange mr-2" aria-hidden="true" />
              Former Presidents
            </h2>
            {isAdmin && (
              <button
                type="button"
                onClick={() => openFormerModal()}
                className="bg-orange hover:bg-orange-dark text-white px-4 py-1.5 rounded-full text-sm font-semibold transition"
              >
                <i className="fas fa-plus mr-1" aria-hidden="true" /> Add Former
                President
              </button>
            )}
          </div>

          {formerPresidents.length === 0 ? (
            <EmptyState
              icon="fa-landmark"
              title="No former presidents added yet"
              hint={
                isAdmin
                  ? 'Click "Add Former President" to add one'
                  : 'Historical records will appear here'
              }
            />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
              {formerPresidents.map((pres) => (
                <motion.div
                  key={pres.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ y: -5 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-md hover:shadow-xl transition-all overflow-hidden group relative cursor-pointer text-center p-5"
                  onDoubleClick={() => showDetail('former', pres)}
                >
                  {isAdmin && (
                    <div className="absolute top-2 right-2 flex gap-1 z-10">
                      <button
                        type="button"
                        onClick={() => openFormerModal(pres)}
                        className="bg-orange/20 hover:bg-orange text-gray-800 dark:text-white p-1 rounded-full text-xs transition"
                        aria-label="Edit Former President"
                      >
                        <i className="fas fa-edit" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteFormer(pres)}
                        className="bg-red-500/20 hover:bg-red-500 text-white p-1 rounded-full text-xs transition"
                        aria-label="Delete Former President"
                      >
                        <i className="fas fa-trash-alt" aria-hidden="true" />
                      </button>
                    </div>
                  )}
                  <img
                    src={
                      pres.imageUrl ||
                      `${PLACEHOLDER}/120x120?text=Former+President`
                    }
                    alt={pres.name}
                    loading="lazy"
                    className="w-28 h-28 rounded-full mx-auto object-cover border-2 border-orange mb-3"
                  />
                  <div className="font-bold text-gray-800 dark:text-white">
                    {pres.name}
                  </div>
                  <div className="text-xs text-orange mt-1">
                    <i className="fas fa-calendar-alt" aria-hidden="true" />{' '}
                    {pres.year}
                  </div>
                  {pres.linkedin && (
                    <a
                      href={normalizeUrl(pres.linkedin)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 mt-2 text-[11px] text-orange hover:underline"
                      aria-label={`${pres.name} LinkedIn`}
                    >
                      <i className="fab fa-linkedin" aria-hidden="true" /> Profile
                    </a>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ==================== MODALS ==================== */}

      <AnimatePresence>
        {presidentModal && (
          <Modal
            onClose={() => setPresidentModal(false)}
            title={hasPresident ? 'Edit President' : 'Add President'}
          >
            <FormField
              label="Full Name *"
              value={presidentForm.name}
              onChange={(v) =>
                setPresidentForm({ ...presidentForm, name: v })
              }
            />
            <FormField
              label="Education"
              value={presidentForm.education}
              onChange={(v) =>
                setPresidentForm({ ...presidentForm, education: v })
              }
            />
            <FormField
              label="Current Position"
              value={presidentForm.position}
              onChange={(v) =>
                setPresidentForm({ ...presidentForm, position: v })
              }
            />
            <FormField
              label="Credentials"
              value={presidentForm.credentials}
              onChange={(v) =>
                setPresidentForm({ ...presidentForm, credentials: v })
              }
            />
            <FormField
              label="LinkedIn / Website URL"
              value={presidentForm.linkedin}
              onChange={(v) =>
                setPresidentForm({ ...presidentForm, linkedin: v })
              }
              placeholder="https://linkedin.com/in/username"
            />
            <PhotoUpload
              label="Photo"
              onFileSelect={(file) =>
                setPresidentForm({ ...presidentForm, photoFile: file })
              }
              preview={presidentForm.photoFile || boardData.president?.photoUrl}
            />
            <div className="flex gap-3 mt-4">
              <button
                type="button"
                onClick={handlePresidentSave}
                disabled={submitting}
                className="flex-1 bg-orange text-white py-2 rounded-lg disabled:opacity-50 hover:bg-orange-dark transition"
              >
                {submitting ? (
                  <>
                    <i
                      className="fas fa-spinner fa-spin mr-1"
                      aria-hidden="true"
                    />
                    Saving…
                  </>
                ) : (
                  'Save'
                )}
              </button>
              <button
                type="button"
                onClick={() => setPresidentModal(false)}
                className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition"
              >
                Cancel
              </button>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {vpModal && (
          <Modal onClose={() => setVpModal(false)} title="Add Vice President">
            <FormField
              label="Title (e.g., VP Internal)"
              value={vpForm.title}
              onChange={(v) => setVpForm({ ...vpForm, title: v })}
            />
            <FormField
              label="Name"
              value={vpForm.name}
              onChange={(v) => setVpForm({ ...vpForm, name: v })}
            />
            <FormField
              label="Education"
              value={vpForm.education}
              onChange={(v) => setVpForm({ ...vpForm, education: v })}
            />
            <FormField
              label="Position/Company"
              value={vpForm.position}
              onChange={(v) => setVpForm({ ...vpForm, position: v })}
            />
            <FormField
              label="Credentials"
              value={vpForm.credentials}
              onChange={(v) => setVpForm({ ...vpForm, credentials: v })}
            />
            <FormField
              label="LinkedIn / Website URL"
              value={vpForm.linkedin}
              onChange={(v) => setVpForm({ ...vpForm, linkedin: v })}
              placeholder="https://linkedin.com/in/username"
            />
            <PhotoUpload
              label="Photo"
              onFileSelect={(file) => setVpForm({ ...vpForm, photoFile: file })}
              preview={vpForm.photoFile}
            />
            <div className="flex gap-3 mt-4">
              <button
                type="button"
                onClick={handleAddVp}
                disabled={submitting}
                className="flex-1 bg-orange text-white py-2 rounded-lg disabled:opacity-50 hover:bg-orange-dark transition"
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => setVpModal(false)}
                className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition"
              >
                Cancel
              </button>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {editVpModal && (
          <Modal
            onClose={() => setEditVpModal(false)}
            title="Edit Vice President"
          >
            <FormField
              label="Title"
              value={editVpForm.title}
              onChange={(v) => setEditVpForm({ ...editVpForm, title: v })}
            />
            <FormField
              label="Name"
              value={editVpForm.name}
              onChange={(v) => setEditVpForm({ ...editVpForm, name: v })}
            />
            <FormField
              label="Education"
              value={editVpForm.education}
              onChange={(v) => setEditVpForm({ ...editVpForm, education: v })}
            />
            <FormField
              label="Position"
              value={editVpForm.position}
              onChange={(v) => setEditVpForm({ ...editVpForm, position: v })}
            />
            <FormField
              label="Credentials"
              value={editVpForm.credentials}
              onChange={(v) =>
                setEditVpForm({ ...editVpForm, credentials: v })
              }
            />
            <FormField
              label="LinkedIn / Website URL"
              value={editVpForm.linkedin}
              onChange={(v) => setEditVpForm({ ...editVpForm, linkedin: v })}
              placeholder="https://linkedin.com/in/username"
            />
            <PhotoUpload
              label="New Photo (optional)"
              onFileSelect={(file) =>
                setEditVpForm({ ...editVpForm, photoFile: file })
              }
              preview={editVpForm.photoFile}
            />
            <div className="flex gap-3 mt-4">
              <button
                type="button"
                onClick={handleUpdateVp}
                disabled={submitting}
                className="flex-1 bg-orange text-white py-2 rounded-lg disabled:opacity-50 hover:bg-orange-dark transition"
              >
                Update
              </button>
              <button
                type="button"
                onClick={() => setEditVpModal(false)}
                className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition"
              >
                Cancel
              </button>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {officerModal && (
          <Modal onClose={() => setOfficerModal(false)} title="Add Officer">
            <FormField
              label="Position Title"
              value={officerForm.position}
              onChange={(v) => setOfficerForm({ ...officerForm, position: v })}
            />
            <FormField
              label="Name"
              value={officerForm.name}
              onChange={(v) => setOfficerForm({ ...officerForm, name: v })}
            />
            <FormField
              label="Education"
              value={officerForm.education}
              onChange={(v) => setOfficerForm({ ...officerForm, education: v })}
            />
            <FormField
              label="Current Position"
              value={officerForm.currentPosition}
              onChange={(v) =>
                setOfficerForm({ ...officerForm, currentPosition: v })
              }
            />
            <FormField
              label="Credentials"
              value={officerForm.credentials}
              onChange={(v) =>
                setOfficerForm({ ...officerForm, credentials: v })
              }
            />
            <FormField
              label="LinkedIn / Website URL"
              value={officerForm.linkedin}
              onChange={(v) => setOfficerForm({ ...officerForm, linkedin: v })}
              placeholder="https://linkedin.com/in/username"
            />
            <PhotoUpload
              label="Photo"
              onFileSelect={(file) =>
                setOfficerForm({ ...officerForm, photoFile: file })
              }
              preview={officerForm.photoFile}
            />
            <div className="flex gap-3 mt-4">
              <button
                type="button"
                onClick={handleAddOfficer}
                disabled={submitting}
                className="flex-1 bg-orange text-white py-2 rounded-lg disabled:opacity-50 hover:bg-orange-dark transition"
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => setOfficerModal(false)}
                className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition"
              >
                Cancel
              </button>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {editOfficerModal && (
          <Modal onClose={() => setEditOfficerModal(false)} title="Edit Officer">
            <FormField
              label="Position"
              value={editOfficerForm.position}
              onChange={(v) =>
                setEditOfficerForm({ ...editOfficerForm, position: v })
              }
            />
            <FormField
              label="Name"
              value={editOfficerForm.name}
              onChange={(v) =>
                setEditOfficerForm({ ...editOfficerForm, name: v })
              }
            />
            <FormField
              label="Education"
              value={editOfficerForm.education}
              onChange={(v) =>
                setEditOfficerForm({ ...editOfficerForm, education: v })
              }
            />
            <FormField
              label="Current Position"
              value={editOfficerForm.currentPosition}
              onChange={(v) =>
                setEditOfficerForm({ ...editOfficerForm, currentPosition: v })
              }
            />
            <FormField
              label="Credentials"
              value={editOfficerForm.credentials}
              onChange={(v) =>
                setEditOfficerForm({ ...editOfficerForm, credentials: v })
              }
            />
            <FormField
              label="LinkedIn / Website URL"
              value={editOfficerForm.linkedin}
              onChange={(v) =>
                setEditOfficerForm({ ...editOfficerForm, linkedin: v })
              }
              placeholder="https://linkedin.com/in/username"
            />
            <PhotoUpload
              label="New Photo (optional)"
              onFileSelect={(file) =>
                setEditOfficerForm({ ...editOfficerForm, photoFile: file })
              }
              preview={editOfficerForm.photoFile}
            />
            <div className="flex gap-3 mt-4">
              <button
                type="button"
                onClick={handleUpdateOfficer}
                disabled={submitting}
                className="flex-1 bg-orange text-white py-2 rounded-lg disabled:opacity-50 hover:bg-orange-dark transition"
              >
                Update
              </button>
              <button
                type="button"
                onClick={() => setEditOfficerModal(false)}
                className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition"
              >
                Cancel
              </button>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {specialModal && (
          <Modal
            onClose={() => setSpecialModal(false)}
            title="Add Special Position"
          >
            <FormField
              label="Position Title"
              value={specialForm.position}
              onChange={(v) => setSpecialForm({ ...specialForm, position: v })}
            />
            <FormField
              label="Name"
              value={specialForm.name}
              onChange={(v) => setSpecialForm({ ...specialForm, name: v })}
            />
            <FormField
              label="Education"
              value={specialForm.education}
              onChange={(v) => setSpecialForm({ ...specialForm, education: v })}
            />
            <FormField
              label="Current Position"
              value={specialForm.currentPosition}
              onChange={(v) =>
                setSpecialForm({ ...specialForm, currentPosition: v })
              }
            />
            <FormField
              label="Credentials"
              value={specialForm.credentials}
              onChange={(v) =>
                setSpecialForm({ ...specialForm, credentials: v })
              }
            />
            <FormField
              label="LinkedIn / Website URL"
              value={specialForm.linkedin}
              onChange={(v) => setSpecialForm({ ...specialForm, linkedin: v })}
              placeholder="https://linkedin.com/in/username"
            />
            <PhotoUpload
              label="Photo"
              onFileSelect={(file) =>
                setSpecialForm({ ...specialForm, photoFile: file })
              }
              preview={specialForm.photoFile}
            />
            <div className="flex gap-3 mt-4">
              <button
                type="button"
                onClick={handleAddSpecial}
                disabled={submitting}
                className="flex-1 bg-orange text-white py-2 rounded-lg disabled:opacity-50 hover:bg-orange-dark transition"
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => setSpecialModal(false)}
                className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition"
              >
                Cancel
              </button>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {editSpecialModal && (
          <Modal
            onClose={() => setEditSpecialModal(false)}
            title="Edit Special Position"
          >
            <FormField
              label="Position"
              value={editSpecialForm.position}
              onChange={(v) =>
                setEditSpecialForm({ ...editSpecialForm, position: v })
              }
            />
            <FormField
              label="Name"
              value={editSpecialForm.name}
              onChange={(v) =>
                setEditSpecialForm({ ...editSpecialForm, name: v })
              }
            />
            <FormField
              label="Education"
              value={editSpecialForm.education}
              onChange={(v) =>
                setEditSpecialForm({ ...editSpecialForm, education: v })
              }
            />
            <FormField
              label="Current Position"
              value={editSpecialForm.currentPosition}
              onChange={(v) =>
                setEditSpecialForm({ ...editSpecialForm, currentPosition: v })
              }
            />
            <FormField
              label="Credentials"
              value={editSpecialForm.credentials}
              onChange={(v) =>
                setEditSpecialForm({ ...editSpecialForm, credentials: v })
              }
            />
            <FormField
              label="LinkedIn / Website URL"
              value={editSpecialForm.linkedin}
              onChange={(v) =>
                setEditSpecialForm({ ...editSpecialForm, linkedin: v })
              }
              placeholder="https://linkedin.com/in/username"
            />
            <PhotoUpload
              label="New Photo (optional)"
              onFileSelect={(file) =>
                setEditSpecialForm({ ...editSpecialForm, photoFile: file })
              }
              preview={editSpecialForm.photoFile}
            />
            <div className="flex gap-3 mt-4">
              <button
                type="button"
                onClick={handleUpdateSpecial}
                disabled={submitting}
                className="flex-1 bg-orange text-white py-2 rounded-lg disabled:opacity-50 hover:bg-orange-dark transition"
              >
                Update
              </button>
              <button
                type="button"
                onClick={() => setEditSpecialModal(false)}
                className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition"
              >
                Cancel
              </button>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {formerModalOpen && (
          <Modal
            onClose={() => setFormerModalOpen(false)}
            title={
              editingFormer ? 'Edit Former President' : 'Add Former President'
            }
            size="max-w-md"
          >
            <div className="space-y-4">
              <FormField
                label="Full Name"
                value={formerForm.name}
                onChange={(v) => setFormerForm({ ...formerForm, name: v })}
              />
              <FormField
                label="Term Year (e.g., 2023-2024 or 2023)"
                value={formerForm.year}
                onChange={(v) => setFormerForm({ ...formerForm, year: v })}
              />
              <FormField
                label="LinkedIn / Website URL"
                value={formerForm.linkedin}
                onChange={(v) => setFormerForm({ ...formerForm, linkedin: v })}
                placeholder="https://linkedin.com/in/username"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Photo
                </label>
                <div
                  className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 text-center cursor-pointer hover:border-orange transition"
                  onClick={() =>
                    document.getElementById('formerImageInput').click()
                  }
                >
                  <i
                    className="fas fa-cloud-upload-alt text-orange text-xl mb-1 block"
                    aria-hidden="true"
                  />
                  <span className="text-sm">Click to upload image</span>
                  <input
                    id="formerImageInput"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFormerImageChange}
                  />
                </div>
                {formerForm.imagePreview && (
                  <img
                    src={formerForm.imagePreview}
                    className="mt-2 h-24 w-24 rounded-full object-cover mx-auto"
                    alt="preview"
                  />
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleSaveFormer}
                  disabled={submittingFormer}
                  className="flex-1 bg-orange text-white py-2 rounded-lg disabled:opacity-50 hover:bg-orange-dark transition"
                >
                  {submittingFormer ? (
                    <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                  ) : editingFormer ? (
                    'Update'
                  ) : (
                    'Add'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setFormerModalOpen(false)}
                  className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      {/* ==================== THEME COLOR MODAL ==================== */}
      <AnimatePresence>
        {colorModalOpen && (
          <Modal
            onClose={() => setColorModalOpen(false)}
            title={`Accent Color · ${currentYear}`}
            size="max-w-md"
          >
            <div className="space-y-5">
              {/* Presets grid */}
              <div>
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Presets
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {THEME_PRESETS.map((preset) => {
                    const isActive = colorDraft.primary === preset.primary;
                    return (
                      <button
                        key={preset.name}
                        type="button"
                        onClick={() => setColorDraft({ ...preset })}
                        aria-label={preset.name}
                        aria-pressed={isActive}
                        title={preset.name}
                        className={`relative h-12 rounded-lg border-2 transition ${
                          isActive
                            ? 'border-gray-900 dark:border-white'
                            : 'border-transparent hover:border-gray-300'
                        }`}
                        style={{ background: preset.primary }}
                      >
                        {isActive && (
                          <i
                            className="fas fa-check text-white absolute inset-0 flex items-center justify-center"
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Custom color */}
              <div>
                <label
                  htmlFor="custom-primary-color"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Custom primary color
                </label>
                <div className="flex items-center gap-3">
                  <input
                    id="custom-primary-color"
                    type="color"
                    value={
                      /^#[0-9a-f]{6}$/i.test(colorDraft.primary)
                        ? colorDraft.primary
                        : DEFAULT_THEME.primary
                    }
                    onChange={(e) => {
                      const primary = e.target.value;
                      setColorDraft({
                        primary,
                        primaryDark: shade(primary, -15),
                        primaryLight: shade(primary, 20),
                      });
                    }}
                    className="w-14 h-10 rounded cursor-pointer border border-gray-300 dark:border-gray-600 bg-transparent"
                    aria-label="Pick primary color"
                  />
                  <input
                    type="text"
                    value={colorDraft.primary}
                    onChange={(e) => {
                      const primary = e.target.value;
                      setColorDraft({
                        primary,
                        primaryDark: shade(primary, -15),
                        primaryLight: shade(primary, 20),
                      });
                    }}
                    className="flex-1 p-2 border rounded-lg text-sm font-mono dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
                    placeholder="#f97316"
                  />
                </div>
              </div>

              {/* Live preview */}
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900">
                <div className="text-xs text-gray-500 mb-2">Preview</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className="px-3 py-1.5 rounded-full text-white text-sm font-semibold"
                    style={{ background: colorDraft.primary }}
                  >
                    Add Member
                  </span>
                  <span
                    className="px-3 py-1.5 rounded-full text-sm font-semibold border-2"
                    style={{
                      borderColor: colorDraft.primary,
                      color: colorDraft.primary,
                    }}
                  >
                    Outline
                  </span>
                  <span
                    className="text-sm font-semibold"
                    style={{ color: colorDraft.primary }}
                  >
                    Text
                  </span>
                  <span
                    className="inline-block w-8 h-8 rounded-full border-2"
                    style={{
                      borderColor: colorDraft.primary,
                      background: `${colorDraft.primary}22`,
                    }}
                    aria-hidden="true"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setColorDraft({ ...DEFAULT_THEME })}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={handleSaveColor}
                  className="flex-1 bg-orange hover:bg-orange-dark text-white py-2 rounded-lg font-semibold transition"
                >
                  Save Color
                </button>
                <button
                  type="button"
                  onClick={() => setColorModalOpen(false)}
                  className="px-4 py-2 rounded-lg text-sm bg-gray-500 text-white hover:bg-gray-600 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {detailModal && detailData && (
          <Modal
            onClose={() => setDetailModal(false)}
            title={
              detailData.type === 'president'
                ? 'President Details'
                : 'Member Details'
            }
            size="max-w-md"
          >
            <div className="text-center">
              <img
                src={
                  detailData.photoUrl ||
                  detailData.imageUrl ||
                  `${PLACEHOLDER}/150x150`
                }
                className="w-32 h-32 rounded-full mx-auto border-4 border-orange mb-3 object-cover"
                alt={detailData.name}
              />
              <h3 className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {detailData.name}
              </h3>
              <p className="text-orange font-semibold mt-1">
                {detailData.title || detailData.position || detailData.year}
              </p>
              <div className="mt-4 text-left space-y-2 text-sm">
                {detailData.education && (
                  <div>
                    <strong>Education:</strong> {detailData.education}
                  </div>
                )}
                {(detailData.position || detailData.currentPosition) && (
                  <div>
                    <strong>Position:</strong>{' '}
                    {detailData.position || detailData.currentPosition}
                  </div>
                )}
                {detailData.credentials && (
                  <div>
                    <strong>Credentials:</strong> {detailData.credentials}
                  </div>
                )}
                {detailData.year && (
                  <div>
                    <strong>Term:</strong> {detailData.year}
                  </div>
                )}
                {detailData.linkedin && (
                  <div>
                    <strong>LinkedIn / Website:</strong>{' '}
                    <a
                      href={normalizeUrl(detailData.linkedin)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-orange hover:underline break-all inline-flex items-center gap-1"
                    >
                      <i className="fab fa-linkedin" aria-hidden="true" />
                      {detailData.linkedin}
                    </a>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <button
                type="button"
                onClick={() => setDetailModal(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition"
              >
                Close
              </button>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        danger={confirm?.danger}
        onCancel={() => setConfirm(null)}
        onConfirm={() => confirm?.onConfirm?.()}
      />

      <PromptDialog
        open={!!prompt}
        title={prompt?.title}
        placeholder={prompt?.placeholder}
        validate={prompt?.validate}
        onSubmit={(v) => prompt?.onSubmit?.(v)}
        onCancel={() => setPrompt(null)}
      />
    </div>
  );
};

/* ================================================================== */
/* Helper components                                                   */
/* ================================================================== */

const Modal = ({ children, onClose, title, size = 'max-w-md' }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
        className={`bg-white dark:bg-gray-800 rounded-2xl ${size} w-full max-h-[90vh] overflow-y-auto p-6`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-800 dark:text-white">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition"
            aria-label="Close"
          >
            <i className="fas fa-times text-xl" aria-hidden="true" />
          </button>
        </div>
        {children}
      </motion.div>
    </motion.div>
  );
};

const FormField = ({
  label,
  value,
  onChange,
  type = 'text',
  rows = null,
  placeholder = '',
}) => (
  <div className="mb-3">
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
      {label}
    </label>
    {rows ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
      />
    )}
  </div>
);

const PhotoUpload = ({ label, onFileSelect, preview }) => {
  const inputId = `photo-upload-${label.replace(/\s+/g, '-').toLowerCase()}`;

  const previewUrl = useMemo(() => {
    if (!preview) return '';
    if (typeof preview === 'string') return preview;
    return URL.createObjectURL(preview);
  }, [preview]);

  useEffect(() => {
    return () => {
      if (preview && typeof preview !== 'string' && previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [preview, previewUrl]);

  return (
    <div className="mb-3">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
      </label>
      <div
        className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 text-center cursor-pointer hover:border-orange transition"
        onClick={() => document.getElementById(inputId)?.click()}
      >
        <i
          className="fas fa-cloud-upload-alt text-orange text-xl mb-1 block"
          aria-hidden="true"
        />
        <span className="text-sm">Click to upload</span>
        <input
          id={inputId}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => onFileSelect(e.target.files[0])}
        />
      </div>
      {previewUrl && (
        <img
          src={previewUrl}
          className="mt-2 h-20 w-20 rounded-full object-cover mx-auto"
          alt="preview"
        />
      )}
    </div>
  );
};

export default Board;